/*
 * HomeFixer360 - Customer Proposal Viewer
 * Copyright (c) 2025 Guy T. Dunn Sr. / Idea Guy Brands Incorporated
 * All Rights Reserved.
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Home, Mail, Phone, Download, CheckCircle, XCircle, 
  MapPin, DollarSign, FileText, Calendar, Loader
} from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerProposal = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [proposal, setProposal] = useState(null);
  const [error, setError] = useState(null);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [notes, setNotes] = useState('');
  const [actionComplete, setActionComplete] = useState(false);
  const [actionType, setActionType] = useState('');

  useEffect(() => {
    loadProposal();
  }, [token]);

  const loadProposal = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/customer-portal/proposal/${token}`);
      setProposal(response.data);
      setError(null);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('This proposal link is invalid or has expired.');
      } else if (err.response?.status === 410) {
        setError('This proposal link has expired (valid for 30 days).');
      } else {
        setError('Unable to load proposal. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action) => {
    try {
      setActionInProgress(true);
      await axios.post(`${BACKEND_URL}/api/customer-portal/proposal/${token}/action`, {
        action,
        notes: notes || null
      });
      setActionComplete(true);
      setActionType(action);
    } catch (err) {
      alert(`Error ${action}ing proposal. Please try again.`);
    } finally {
      setActionInProgress(false);
    }
  };

  const handleDownloadPDF = () => {
    window.open(`${BACKEND_URL}/api/customer-portal/proposal/${token}/pdf`, '_blank');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading your proposal...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-8 text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Unable to Load Proposal</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (actionComplete) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-8 text-center">
          {actionType === 'accept' ? (
            <>
              <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-gray-900 mb-4">🎉 Proposal Accepted!</h2>
              <p className="text-gray-600 mb-6">
                Thank you for accepting our proposal! We've notified your contractor and they will 
                contact you shortly to schedule the installation.
              </p>
            </>
          ) : (
            <>
              <XCircle className="w-20 h-20 text-gray-500 mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Proposal Declined</h2>
              <p className="text-gray-600 mb-6">
                We've received your response. Your contractor may reach out to discuss any concerns or 
                provide alternative options.
              </p>
            </>
          )}
          
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              You will receive a confirmation email shortly.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const { estimate, company, customer } = proposal;
  const isAccepted = estimate.accepted;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-orange-500 text-white rounded-t-2xl p-8">
          {company.logo_url && (
            <img 
              src={company.logo_url} 
              alt={company.company_name}
              className="h-16 mb-4"
            />
          )}
          <h1 className="text-3xl font-bold mb-2">{company.company_name}</h1>
          <p className="text-blue-100">Professional Roofing Proposal</p>
        </div>

        {/* Main Content */}
        <div className="bg-white shadow-xl rounded-b-2xl p-8">
          {/* Customer & Property Info */}
          <div className="mb-8 pb-8 border-b">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Proposal Details</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center text-gray-700 mb-2">
                  <Home className="w-5 h-5 mr-2 text-blue-600" />
                  <span className="font-semibold">Property Address:</span>
                </div>
                <p className="text-gray-600 ml-7">{estimate.address}</p>
              </div>
              <div>
                <div className="flex items-center text-gray-700 mb-2">
                  <Mail className="w-5 h-5 mr-2 text-blue-600" />
                  <span className="font-semibold">Your Email:</span>
                </div>
                <p className="text-gray-600 ml-7">{customer.email}</p>
              </div>
            </div>
          </div>

          {/* Roof Measurements */}
          <div className="mb-8 pb-8 border-b">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Roof Measurements</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Total Area</p>
                <p className="text-2xl font-bold text-blue-600">
                  {estimate.satellite_data?.square_footage?.toLocaleString() || 0} sq ft
                </p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Roof Pitch</p>
                <p className="text-2xl font-bold text-blue-600">
                  {estimate.satellite_data?.pitch || 0}:12
                </p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Complexity</p>
                <p className="text-2xl font-bold text-blue-600 capitalize">
                  {estimate.satellite_data?.complexity || 'Standard'}
                </p>
              </div>
            </div>
          </div>

          {/* Pricing Breakdown */}
          <div className="mb-8 pb-8 border-b">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Cost Breakdown</h2>
            <div className="space-y-3">
              <div className="flex justify-between py-2">
                <span className="text-gray-700">Materials Cost:</span>
                <span className="font-semibold">{formatCurrency(estimate.pricing?.materials)}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-700">Labor Cost:</span>
                <span className="font-semibold">{formatCurrency(estimate.pricing?.labor)}</span>
              </div>
              <div className="flex justify-between py-2 border-t">
                <span className="text-gray-700">Subtotal:</span>
                <span className="font-semibold">{formatCurrency(estimate.pricing?.subtotal)}</span>
              </div>

              {estimate.pricing?.sales_tax_rate > 0 && (
                <div className="flex justify-between py-2">
                  <span className="text-gray-700">Sales Tax ({estimate.pricing.sales_tax_rate}%):</span>
                  <span className="font-semibold">{formatCurrency(estimate.pricing?.sales_tax_amount)}</span>
                </div>
              )}

              {estimate.pricing?.steep_roof_applied && estimate.pricing?.steep_roof_charge > 0 && (
                <div className="flex justify-between py-2">
                  <span className="text-gray-700">Steep Roof Charge:</span>
                  <span className="font-semibold">{formatCurrency(estimate.pricing.steep_roof_charge)}</span>
                </div>
              )}

              {estimate.additional_charges?.breakdown?.length > 0 && (
                <>
                  {estimate.additional_charges.breakdown.map((charge, i) => (
                    <div key={i} className="flex justify-between py-2">
                      <span className="text-gray-700">{charge.label || charge.description}:</span>
                      <span className="font-semibold">{formatCurrency(charge.amount)}</span>
                    </div>
                  ))}
                  {estimate.additional_charges.sales_tax_on_charges > 0 && (
                    <div className="flex justify-between py-2">
                      <span className="text-gray-700">Tax on Additional Charges:</span>
                      <span className="font-semibold">{formatCurrency(estimate.additional_charges.sales_tax_on_charges)}</span>
                    </div>
                  )}
                </>
              )}
              
              {estimate.discount_percent > 0 && (
                <div className="flex justify-between py-2 text-green-600">
                  <span>Discount ({estimate.discount_percent}%):</span>
                  <span className="font-semibold">
                    -{formatCurrency((estimate.total_cost || estimate.pricing?.total) * estimate.discount_percent / 100)}
                  </span>
                </div>
              )}

              <div className="flex justify-between py-4 border-t-2 border-gray-300">
                <span className="text-xl font-bold text-gray-900">Total Investment:</span>
                <span className="text-2xl font-bold text-blue-600">
                  {formatCurrency(
                    estimate.discounted_price || estimate.total_cost || estimate.pricing?.total
                  )}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          {!isAccepted && (
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Your Decision</h2>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows="3"
                  placeholder="Any questions or comments for your contractor..."
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <Button
                  onClick={() => handleAction('accept')}
                  disabled={actionInProgress}
                  className="bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-semibold"
                >
                  {actionInProgress ? (
                    <Loader className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <CheckCircle className="w-5 h-5 mr-2" />
                  )}
                  Accept Proposal
                </Button>
                <Button
                  onClick={() => handleAction('decline')}
                  disabled={actionInProgress}
                  variant="outline"
                  className="border-2 border-gray-300 hover:bg-gray-100 py-6 text-lg font-semibold"
                >
                  <XCircle className="w-5 h-5 mr-2" />
                  Decline
                </Button>
              </div>
            </div>
          )}

          {isAccepted && (
            <div className="mb-8 p-6 bg-green-50 border-2 border-green-200 rounded-lg">
              <div className="flex items-center mb-2">
                <CheckCircle className="w-6 h-6 text-green-600 mr-2" />
                <h3 className="text-lg font-bold text-green-900">Proposal Accepted</h3>
              </div>
              <p className="text-green-700">
                This proposal has been accepted. Your contractor will contact you soon to schedule the installation.
              </p>
            </div>
          )}

          {/* Download PDF */}
          <div className="text-center">
            <Button
              onClick={handleDownloadPDF}
              variant="outline"
              className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50"
            >
              <Download className="w-5 h-5 mr-2" />
              Download PDF Proposal
            </Button>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-8 border-t text-center">
            <div className="flex justify-center items-center space-x-6 text-sm text-gray-600">
              {company.phone && (
                <div className="flex items-center">
                  <Phone className="w-4 h-4 mr-1" />
                  {company.phone}
                </div>
              )}
              {company.email && (
                <div className="flex items-center">
                  <Mail className="w-4 h-4 mr-1" />
                  {company.email}
                </div>
              )}
            </div>
            
            {company.subscription_tier === 'starter' && (
              <p className="text-xs text-gray-500 mt-4">
                Created with HomeFixer360 - Professional Roofing Estimation Software
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerProposal;
